// COMP-NOTIFICATION-CENTER — تنظیمات اعلان (US-MSG-061، AC-MSG-061.1؛ AP-MSG-02): برای هر نوع اعلان کانال‌های درون‌برنامه، Push و پیامک؛
// نوع‌های همیشه بحرانی قفل‌اند (PHX-MSG-0703) و شدت Critical در هر نوع همیشه از همه کانال‌ها می‌رسد (BR-MSG-060). Push این دستگاه
// (US-MSG-002، AC-MSG-002.2) با Service Worker و کلید VAPID سرور فعال یا لغو می‌شود.
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type NotificationPreferenceView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormDialog, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { COMP_NOTIFICATION_CENTER, msgKeys } from './common';
import { usePushDevice } from './push';

type Channel = 'inApp' | 'push' | 'sms';
const CHANNELS: Channel[] = ['inApp', 'push', 'sms'];
const CHANNEL_LABELS: Record<Channel, string> = {
  inApp: 'msg:notifications.in_app',
  push: 'msg:notifications.push',
  sms: 'msg:notifications.sms',
};

/** Push همین دستگاه. */
function PushDevicePanel({ available, devices }: { available: boolean; devices: number }) {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const device = usePushDevice();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const supported = device.supported();

  useEffect(() => {
    let active = true;
    if (supported) {
      void device
        .current()
        .then((current) => active && setEnabled(current !== null))
        .catch(() => active && setEnabled(false));
    }
    return () => {
      active = false;
    };
  }, [device, supported]);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: msgKeys.preferences(session?.tenantId) });
  const enable = useMutation({
    mutationFn: async () => {
      const config = await unwrap(tenantApi.GET('/api/v1/messaging/push/config'));
      if (!config.available || !config.publicKey) {
        throw new Error('push_unavailable');
      }
      const subscription = await device.subscribe(config.publicKey, {
        title: t('msg:notifications.push_title'),
        body: t('msg:notifications.push_body'),
        critical: t('msg:notifications.push_body_critical'),
        message: t('msg:notifications.push_message_body'),
      });
      return unwrap(
        tenantApi.POST('/api/v1/messaging/push-subscriptions', {
          body: {
            endpoint: subscription.endpoint,
            keys: subscription.keys,
            deviceLabel: navigator.userAgent.slice(0, 60),
          },
        }),
      );
    },
    onSuccess: () => {
      setEnabled(true);
      refresh();
    },
  });
  const disable = useMutation({
    mutationFn: async () => {
      const current = await device.current();
      if (current) {
        const view = await unwrap(
          tenantApi.POST('/api/v1/messaging/push-subscriptions', {
            body: { endpoint: current.endpoint, keys: current.keys, deviceLabel: null },
          }),
        );
        await unwrap(
          tenantApi.POST('/api/v1/messaging/push-subscriptions/{id}:revoke', {
            params: { path: { id: view.id } },
          }),
        );
      }
      await device.unsubscribe();
    },
    onSuccess: () => {
      setEnabled(false);
      refresh();
    },
  });

  let status: string;
  if (!supported) {
    status = 'msg:notifications.push_unsupported';
  } else if (!available) {
    status = 'msg:notifications.push_unavailable';
  } else if (device.permission() === 'denied') {
    status = 'msg:notifications.push_denied';
  } else {
    status = enabled ? 'msg:notifications.push_enabled' : 'msg:notifications.push_disabled';
  }
  const error = enable.error ?? disable.error;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="push-device">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Stack sx={{ flex: 1 }} spacing={0.25}>
          <Typography variant="subtitle2">{t('msg:notifications.device')}</Typography>
          <Typography
            variant="body2"
            data-testid="push-device-status"
            data-enabled={enabled ? 'true' : 'false'}
          >
            {t(status)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('msg:notifications.push_devices', { count: faDigits(String(devices)) })}
          </Typography>
        </Stack>
        {supported && available ? (
          enabled ? (
            <Button onClick={() => disable.mutate()} disabled={disable.isPending} data-testid="push-disable">
              {t('msg:notifications.push_disable')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={() => enable.mutate()}
              disabled={enable.isPending || device.permission() === 'denied'}
              data-testid="push-enable"
            >
              {t('msg:notifications.push_enable')}
            </Button>
          )
        ) : null}
      </Stack>
      {error instanceof Error && error.message === 'push_denied' ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('msg:notifications.push_denied')}
        </Alert>
      ) : error ? (
        <ErrorState error={error} compact />
      ) : null}
    </Paper>
  );
}

/** دیالوگ تنظیمات اعلان من. */
export function NotificationPreferencesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const preferences = useQuery({
    queryKey: msgKeys.preferences(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/notification-preferences')),
  });
  const [draft, setDraft] = useState<Record<string, NotificationPreferenceView>>({});
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/messaging/notification-preferences', {
          body: {
            items: Object.values(draft).map((item) => ({
              type: item.type,
              inApp: item.inApp,
              push: item.push,
              sms: item.sms,
            })),
          },
        }),
      ),
    onSuccess: (view) => {
      queryClient.setQueryData(msgKeys.preferences(session?.tenantId), view);
      setDraft({});
      setSaved(true);
    },
  });

  const rows = (preferences.data?.items ?? []).map((item) => draft[item.type] ?? item);
  const toggle = (item: NotificationPreferenceView, channel: Channel) => {
    setSaved(false);
    setDraft((current) => ({ ...current, [item.type]: { ...item, [channel]: !item[channel] } }));
  };

  return (
    <FormDialog
      open
      titleKey="msg:notifications.preferences_title"
      screenId={COMP_NOTIFICATION_CENTER}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={Object.keys(draft).length > 0}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <Typography variant="body2" color="text.secondary">
        {t('msg:notifications.preferences_hint')}
      </Typography>
      {saved ? (
        <Alert
          severity="success"
          data-testid="notification-preferences-saved"
          onClose={() => setSaved(false)}
        >
          {t('msg:notifications.saved')}
        </Alert>
      ) : null}
      {preferences.data ? (
        <PushDevicePanel
          available={preferences.data.pushAvailable}
          devices={Number(preferences.data.pushDevices)}
        />
      ) : null}
      {preferences.isPending ? <LoadingState rows={4} /> : null}
      {preferences.error ? <ErrorState error={preferences.error} compact /> : null}
      {preferences.data ? (
        <Table size="small" aria-label={t('msg:notifications.preferences_title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('msg:notifications.type')}</TableCell>
              {CHANNELS.map((channel) => (
                <TableCell key={channel} align="center">
                  {t(CHANNEL_LABELS[channel])}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((item) => (
              <TableRow
                key={item.type}
                data-testid={`preference-${item.type}`}
                data-locked={item.alwaysCritical ? 'true' : 'false'}
              >
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>
                      {t(`msg:notification_types.${item.type.replaceAll('.', '_')}`, {
                        name: '…',
                        defaultValue: item.type,
                      })}
                    </span>
                    {item.alwaysCritical ? (
                      <Tooltip title={t('msg:notifications.locked')}>
                        <LockOutlinedIcon fontSize="small" aria-label={t('msg:notifications.locked')} />
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
                {CHANNELS.map((channel) => (
                  <TableCell key={channel} align="center">
                    <Switch
                      checked={item[channel]}
                      onChange={() => toggle(item, channel)}
                      disabled={item.alwaysCritical}
                      slotProps={{
                        input: {
                          'aria-label': `${t(CHANNEL_LABELS[channel])}`,
                          'data-testid': `preference-${item.type}-${channel}`,
                        } as never,
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </FormDialog>
  );
}
